<template>
  <div class="stat-card-wrapper">
    <v-card class="stat-card" :class="{ 'loading-state': loading }" :style="{ borderTopColor: color }">
      <div class="loading-overlay" v-if="loading">
        <div class="skeleton-content">
          <div class="skeleton-label"></div>
          <div class="skeleton-value"></div>
          <div class="skeleton-text"></div>
        </div>
        <div class="skeleton-icon"></div>
      </div>
 
      <div class="card-shimmer" v-if="loading"></div>
 
      <v-card-text class="card-content" :class="{ 'fade-out': loading }">
        <div class="info-section">
          <p class="card-label">{{ title }}</p>
          <h3 class="card-value" :style="{ color }">{{ value }}</h3>
          <p class="card-updated">{{ updatedAt }}</p>
        </div>
        <div class="icon-section">
          <v-avatar class="icon-avatar" :style="{ backgroundColor: color }">
            <v-icon size="32" color="white">{{ icon }}</v-icon>
          </v-avatar>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'StatCard',
  props: {
    title: {
      type: String,
      required: true,
    },
    value: {
      type: [Number, String],
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    color: {
      type: String,
      default: '#0e511a',
    },
    updatedAt: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
.stat-card-wrapper {
  height: 100%;
}

.stat-card {
  position: relative;
  overflow: hidden;
  height: 160px;
  display: flex;
  border-radius: 12px !important;
  background: linear-gradient(135deg, #f5f7f5 0%, #fafaf9 100%);
  cursor: pointer;
  border-top: 4px solid #0e511a;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-card.loading-state {
  cursor: wait;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: -120%;
  width: 70%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.5),
    transparent
  );
  transition: left 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 32px rgba(14, 81, 26, 0.15);
  background: linear-gradient(135deg, #defde7 0%, #f5f7f5 100%);
}

.stat-card:hover::before {
  left: 150%;
}

.stat-card:hover:not(.loading-state) .icon-avatar {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  z-index: 2;
  pointer-events: none;
}

.skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-label {
  width: 60px;
  height: 12px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f0f0f0 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: skeleton-loading 1.5s infinite;
}

.skeleton-value {
  width: 80px;
  height: 32px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f0f0f0 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: skeleton-loading 1.5s infinite 0.1s;
}

.skeleton-text {
  width: 100px;
  height: 11px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f0f0f0 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: skeleton-loading 1.5s infinite 0.2s;
}

.skeleton-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f0f0f0 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite 0.15s;
  margin-left: 16px;
  flex-shrink: 0;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.fade-out {
  opacity: 0;
  pointer-events: none;
}

.card-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.card-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  position: relative;
  z-index: 1;
  padding: 20px !important;
}

.info-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.card-value {
  font-size: 32px;
  font-weight: 700;
  margin: 4px 0 0 0;
  transition: all 0.3s ease;
}

.stat-card:hover .card-value {
  font-size: 36px;
}

.card-updated {
  font-size: 11px;
  color: #999;
  margin: 4px 0 0 0;
}

.icon-section {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 16px;
}

.icon-avatar {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}


@media (max-width: 600px) {
  .stat-card {
    height: 140px;
  }

  .card-value {
    font-size: 28px;
  }

  .stat-card:hover .card-value {
    font-size: 30px;
  }

  .icon-section {
    margin-left: 8px;
  }
}
</style>
